import React, { useEffect, useState } from 'react';
import { Form } from 'antd';
import type { FormInstance } from 'antd';
import { RightOutlined } from '@ant-design/icons';
import CollapsePanel from '@src/components/Common/CollapsePanel';
import { Field } from '@src/components/Common/Form';
import EvaluationCasesField from './EvaluationCasesField';
import EvaluationRunHeader from './EvaluationRunHeader';
import useEvaluationRun from './useEvaluationRun';
import { EvaluationCase } from './types';
import { CaseErrors, hasCaseErrors, NO_CASE_ERRORS } from './utils';
import './Evaluation.css';

/** why the run cannot be started, in the order the reader can act on */
const whyDisabled = ({
    processorId,
    active,
    caseCount,
    polling,
    unsaved,
}: {
    processorId?: number | string;
    active: boolean;
    caseCount: number;
    polling: boolean;
    unsaved: boolean;
}) => {
    if (!processorId) return 'Save the processor before running its test suite.';
    if (!active) return 'The processor is inactive. Activate it from the AI processors list to run the test suite.';
    if (!caseCount) return 'Add at least one test case to run the test suite.';
    /* A run grades the *saved* prompt against the *saved* cases. With the form
       ahead of the server it would quietly test the previous version, and
       report the score as though it were this one. */
    if (unsaved) return 'Save your changes before running — a run uses the saved prompt and test cases.';
    if (polling) return 'A run is already in progress.';
    return null;
};

/**
 * The evaluation of a change instance validator: a test suite, and the last
 * run of it.
 *
 * One card, shut until asked for — a validator is usually opened to change its
 * prompt, and the suite is a whole page of its own underneath that. What the
 * card is worth knowing at a glance, the score and the button that refreshes
 * it, is on the header and so is readable without opening anything.
 */
const EvaluationSection = ({
    form,
    record,
    /** an older version is on screen: what it says is not what would run */
    historical = false,
    readOnly = false,
    /** the form is ahead of the server */
    unsaved = false,
    caseErrors = NO_CASE_ERRORS,
}: {
    form: FormInstance;
    record?: any;
    historical?: boolean;
    readOnly?: boolean;
    unsaved?: boolean;
    caseErrors?: CaseErrors;
}) => {
    /* useWatch only reports paths a mounted field has registered, and the card
       starts shut — so for a suite nobody has opened yet the count comes off
       the store directly. Once it is open the field drives the watch, which is
       the only time these can disagree. */
    const watched: EvaluationCase[] | undefined = Form.useWatch(['extra_data', 'evaluation'], form);
    const cases: EvaluationCase[] = watched ?? form.getFieldValue(['extra_data', 'evaluation']) ?? [];
    const { result, starting, polling, notice, run } = useEvaluationRun(historical ? undefined : record?.id);
    const [open, setOpen] = useState(false);

    /* A save the API turned down over the cases has to be visible, and the
       cases are behind a shut card. */
    useEffect(() => {
        if (hasCaseErrors(caseErrors)) setOpen(true);
    }, [caseErrors]);

    const toggle = () => setOpen((previous) => !previous);

    return (
        <section className={`aip-card${open ? ' aip-card--open' : ''}`} aria-label="Evaluation">
            <div className="aip-card__head">
                {/* the disclosure is a real button in a real heading — the run
                    controls beside it are buttons of their own, and a button
                    inside a button is neither reachable nor announceable */}
                <h3 className="aip-card__heading">
                    <button type="button" className="aip-card__toggle" aria-expanded={open} onClick={toggle}>
                        <RightOutlined className="aip-card__chev" aria-hidden="true" />
                        Evaluation
                        <span className="aip-card__count">
                            {cases.length} {cases.length === 1 ? 'case' : 'cases'}
                        </span>
                    </button>
                </h3>

                {!historical && (
                    <EvaluationRunHeader
                        result={result}
                        blocked={whyDisabled({
                            processorId: record?.id,
                            active: !!record?.active,
                            caseCount: cases.length,
                            polling,
                            unsaved,
                        })}
                        starting={starting}
                        notice={notice}
                        onRun={run}
                    />
                )}
            </div>

            <CollapsePanel open={open} className="aip-card__body">
                <p className="aip-card__lede">
                    A test suite for this validator: declarations it is tried against, each with the verdict it
                    should reach. Running the suite scores the saved prompt against every case.
                </p>

                {/* no initialValue of its own: the form is seeded whole by
                    toFormValues, and a second seed on the same path is one antd
                    warns about and ignores */}
                <Field name={['extra_data', 'evaluation']} noStyle>
                    <EvaluationCasesField result={result} errors={caseErrors} readOnly={readOnly} />
                </Field>
            </CollapsePanel>
        </section>
    );
};

export default EvaluationSection;
